<script>
import { mapGetters } from 'vuex';
import { useAlert, useAPIErrorAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { getRandomColor } from 'dashboard/helper/labelColor';
import { canTogglePrivateLabel } from 'dashboard/helper/labelPrivacy';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import { useVuelidate } from '@vuelidate/core';
import {
  BILLING_FEATURES,
  ensureBillingFeature,
  refreshBillingEntitlements,
} from 'dashboard/composables/useBillingEntitlements';

export default {
  props: {
    prefillTitle: {
      type: String,
      default: '',
    },
    onCreated: {
      type: Function,
      default: null,
    },
    onClose: {
      type: Function,
      default: null,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      color: '#000',
      description: '',
      title: '',
      showOnSidebar: true,
      stagnation_days: 0,
      isPrivate: false,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    // O campo so aparece para quem consegue usa-lo — escondido, e um formulario
    // a menos que volta 403. Quem manda continua sendo `LabelPolicy`.
    canSetPrivate() {
      return canTogglePrivateLabel({
        permissions: getUserPermissions(
          this.currentUser,
          this.currentAccountId
        ),
        userId: this.currentUser?.id,
      });
    },
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
  },
  mounted() {
    this.color = getRandomColor();
    this.title = this.prefillTitle.toLowerCase();
  },
  methods: {
    normalizeCreatedLabel(label = {}) {
      const createdLabel = label?.payload || label;

      return {
        id: createdLabel.id,
        title: createdLabel.title || this.title.toLowerCase(),
        description: createdLabel.description ?? this.description,
        color: createdLabel.color || this.color,
        show_on_sidebar: createdLabel.show_on_sidebar ?? this.showOnSidebar,
        stagnation_days: createdLabel.stagnation_days ?? this.stagnation_days,
        private: createdLabel.private ?? this.isPrivate,
        created_by: createdLabel.created_by ?? null,
      };
    },
    closeModal() {
      if (this.onClose) {
        this.onClose();
      }
      this.$emit('close');
    },
    async addLabel() {
      if (!ensureBillingFeature(this.$store, BILLING_FEATURES.labels)) {
        return;
      }

      try {
        const requestPayload = {
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
          stagnation_days: this.stagnation_days,
          // So vai no payload quando o campo existe na tela: mandar
          // `private: false` sempre faria o servidor cobrar a permissao de
          // privacidade em toda criacao (ver `privacy_change?`).
          ...(this.canSetPrivate ? { private: this.isPrivate } : {}),
        };
        const label = await this.$store.dispatch('labels/create', {
          ...requestPayload,
        });
        const normalizedLabel = this.normalizeCreatedLabel(label);
        refreshBillingEntitlements(this.$store);
        useAlert(this.$t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
        if (this.onCreated) {
          this.onCreated(normalizedLabel);
        }
        // `LabelDropdown.vue` escuta `@label-created` com este nome exato:
        // renomear para camelCase quebra o ouvinte. O `disable` e para o hook de
        // pre-commit, que lint-a o arquivo inteiro a cada commit que o toque.
        // eslint-disable-next-line vue/custom-event-name-casing
        this.$emit('label-created', normalizedLabel);
        this.$emit('created', normalizedLabel);
        this.$emit('create', normalizedLabel);
        this.closeModal();
      } catch (error) {
        useAPIErrorAlert(error, this.$t('LABEL_MGMT.ADD.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="$t('LABEL_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addLabel">
      <woot-input
        v-model.trim="title"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        data-testid="label-title"
        @input="v$.title.$touch"
      />

      <woot-input
        v-model.trim="description"
        :class="{ error: v$.description.$error }"
        class="w-full"
        :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        data-testid="label-description"
        @input="v$.description.$touch"
      />

      <woot-input
        v-model.number="stagnation_days"
        :class="{ error: v$.stagnation_days.$error }"
        class="w-full"
        :label="$t('LABEL_MGMT.FORM.STAGNATION_DAYS.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.STAGNATION_DAYS.PLACEHOLDER')"
        data-testid="label-stagnation-days"
        @input="v$.stagnation_days.$touch"
      />

      <div class="w-full">
        <label>
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="color" />
        </label>
      </div>
      <div v-if="canSetPrivate" class="w-full">
        <div class="flex items-center w-full gap-2">
          <input
            v-model="isPrivate"
            type="checkbox"
            :value="true"
            data-testid="label-private"
          />
          <label>{{ $t('LABEL_MGMT.FORM.PRIVATE.LABEL') }}</label>
        </div>
        <p class="mb-2 text-xs text-muted">
          {{ $t('LABEL_MGMT.FORM.PRIVATE.HELP') }}
        </p>
      </div>
      <div class="flex items-center w-full gap-2">
        <input v-model="showOnSidebar" type="checkbox" :value="true" />
        <label for="conversation_creation">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <woot-button
          :is-disabled="v$.title.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
          data-testid="label-submit"
        >
          {{ $t('LABEL_MGMT.FORM.CREATE') }}
        </woot-button>
        <woot-button class="button clear" @click.prevent="closeModal">
          {{ $t('LABEL_MGMT.FORM.CANCEL') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  ::v-deep {
    input {
      @apply lowercase;
    }
  }
}
</style>
